import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { type GovernanceUnitNode } from "~/api/types";
import { useGovernanceUnits } from "~/api/v2/useGovernanceUnits";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import {
  governanceUnitPathIds,
  isHomeOrDescendant,
} from "~/helpers/governanceUnit";
import { toggleSetItem } from "~/helpers/set";
import { useUserContext } from "~/hooks/useUserContext";
import { cn } from "~/lib/utils";
import ErrorState from "~/molecules/ErrorState";
import HeadingPage from "~/molecules/HeadingPage";
import { TableSkeleton } from "~/molecules/TableSkeleton";
import { CreateGovernanceUnitDialog } from "~/organisms/CreateGovernanceUnitDialog";
import { MoveGovernanceUnitDialog } from "~/organisms/MoveGovernanceUnitDialog";
import { RenameGovernanceUnitDialog } from "~/organisms/RenameGovernanceUnitDialog";

const INDENT_PX_PER_LEVEL = 28;

interface GovernanceUnitRowProps {
  node: GovernanceUnitNode;
  depth: number;
  expandedIds: ReadonlySet<number>;
  onToggle: (id: number) => void;
  onRename: (node: GovernanceUnitNode) => void;
  onMove: (node: GovernanceUnitNode) => void;
}

function GovernanceUnitRow({
  node,
  depth,
  expandedIds,
  onToggle,
  onRename,
  onMove,
}: GovernanceUnitRowProps) {
  const { t } = useTranslation();
  const isExpanded = expandedIds.has(node.id);
  const canManage = isHomeOrDescendant(node);

  return (
    <>
      <TableRow>
        <TableCell>
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: depth * INDENT_PX_PER_LEVEL }}
          >
            {node.children.length > 0 ? (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onToggle(node.id)}
              >
                <ChevronRight
                  className={cn(
                    "transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
              </Button>
            ) : (
              <div className="size-7" />
            )}
            <span className="font-medium">{node.name}</span>
            {node.isHome && (
              <Badge>{t(($) => $.governanceUnits.yourGovernanceUnit)}</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground text-right">
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={() => onRename(node)}>
                    {t(($) => $.governanceUnits.rename)}
                  </DropdownMenuItem>
                  {!node.isHome && (
                    <DropdownMenuItem onSelect={() => onMove(node)}>
                      {t(($) => $.governanceUnits.move)}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
      {isExpanded &&
        node.children.map((child) => (
          <GovernanceUnitRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            onToggle={onToggle}
            onRename={onRename}
            onMove={onMove}
          />
        ))}
    </>
  );
}

interface LoadedGovernanceUnitsProps {
  governanceUnits: GovernanceUnitNode[];
  onChanged: () => void;
}

function LoadedGovernanceUnits({
  governanceUnits,
  onChanged,
}: LoadedGovernanceUnitsProps) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const pathIds = (id: number) =>
    governanceUnits.flatMap((root) => governanceUnitPathIds(root, id));
  // Open down to the home unit, and later down to each unit created here.
  const [expandedIds, setExpandedIds] = useState(
    () => new Set(pathIds(user.homeGovernanceUnitId)),
  );
  const [renameTarget, setRenameTarget] = useState<GovernanceUnitNode | null>(
    null,
  );
  const [moveTarget, setMoveTarget] = useState<GovernanceUnitNode | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between">
        <HeadingPage title={t(($) => $.governanceUnits.title)} />
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          {t(($) => $.governanceUnits.newGovernanceUnit)}
        </Button>
      </div>

      <Table className="bg-card border-border border">
        <TableHeader className="bg-muted">
          <TableRow>
            <TableHead>{t(($) => $.governanceUnits.name)}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {governanceUnits.map((node) => (
            <GovernanceUnitRow
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              onToggle={(id) =>
                setExpandedIds((current) => toggleSetItem(current, id))
              }
              onRename={setRenameTarget}
              onMove={setMoveTarget}
            />
          ))}
        </TableBody>
      </Table>

      <RenameGovernanceUnitDialog
        governanceUnit={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRenamed={onChanged}
      />

      <MoveGovernanceUnitDialog
        governanceUnits={governanceUnits}
        governanceUnit={moveTarget}
        onClose={() => setMoveTarget(null)}
        onMoved={onChanged}
      />

      <CreateGovernanceUnitDialog
        governanceUnits={governanceUnits}
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={(parentGovernanceUnitId) => {
          setExpandedIds(
            (current) =>
              new Set([...current, ...pathIds(parentGovernanceUnitId)]),
          );
          onChanged();
        }}
      />
    </>
  );
}

export default function GovernanceUnitsPage() {
  const { t } = useTranslation();
  const { data: governanceUnits, error, mutate } = useGovernanceUnits();

  let content: ReactNode;
  if (error) {
    content = (
      <>
        <HeadingPage title={t(($) => $.governanceUnits.title)} />
        <ErrorState onRetry={() => void mutate()} />
      </>
    );
  } else if (governanceUnits) {
    content = (
      <LoadedGovernanceUnits
        governanceUnits={governanceUnits}
        onChanged={() => void mutate()}
      />
    );
  } else {
    content = (
      <>
        <HeadingPage title={t(($) => $.governanceUnits.title)} />
        <TableSkeleton rows={3} columns={1} wrapperClassName="bg-card border" />
      </>
    );
  }

  return (
    <div className="container flex grow flex-col gap-6 py-7">{content}</div>
  );
}
